import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Sparkles, X } from 'lucide-react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const POLL_MS = 15_000;
const TTL_MS = 9_000;
const SEEN_KEY = 'betohalics:bigwin_seen';

const loadSeen = () => {
    try {
        const raw = localStorage.getItem(SEEN_KEY);
        return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
};
const saveSeen = (set) => {
    try { localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(set).slice(-100))); } catch { /* ignore */ }
};

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fireBigWinConfetti = (multiplier) => {
    // Bigger blast for higher multipliers
    const particles = Math.min(80 + Math.floor(multiplier * 2), 200);
    const defaults = { startVelocity: 38, spread: 360, ticks: 90, zIndex: 100 };
    const colors = ['#FFD700', '#39FF14', '#FF007F', '#FFFFFF'];
    confetti({ ...defaults, particleCount: particles, origin: { x: 0.2, y: 0.5 }, colors });
    confetti({ ...defaults, particleCount: particles, origin: { x: 0.8, y: 0.5 }, colors });
    setTimeout(() => confetti({ ...defaults, particleCount: particles, origin: { x: 0.5, y: 0.3 }, colors }), 250);
};

/**
 * BigWinCelebration
 * -----------------
 * Polls /api/notifications every 15s for fresh `slot_big_win` entries.
 * When a new one appears, shows a centered banner + confetti blast.
 * Auto-dismisses after 9s; click to dismiss early.
 * Mounted globally inside <App />.
 */
const BigWinCelebration = () => {
    const { user } = useAuth();
    const [active, setActive] = useState(null);
    const seenRef = useRef(loadSeen());
    const initializedRef = useRef(false);
    const dismissTimerRef = useRef(null);

    const dismiss = useCallback(() => {
        clearTimeout(dismissTimerRef.current);
        setActive(null);
    }, []);

    useEffect(() => {
        if (!user || user.role === 'admin') return;
        let cancelled = false;

        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/notifications?limit=20`, { withCredentials: true });
                if (cancelled) return;
                const bigWins = (data?.notifications || []).filter((n) => n.type === 'slot_big_win');

                if (!initializedRef.current) {
                    initializedRef.current = true;
                    bigWins.forEach((n) => seenRef.current.add(n.id));
                    saveSeen(seenRef.current);
                    return;
                }

                // Newest unseen big win (server returns sorted desc by created_at)
                const fresh = bigWins.find((n) => !seenRef.current.has(n.id) && !n.read);
                if (!fresh) return;

                seenRef.current.add(fresh.id);
                saveSeen(seenRef.current);

                setActive(fresh);
                clearTimeout(dismissTimerRef.current);
                dismissTimerRef.current = setTimeout(() => setActive(null), TTL_MS);
                fireBigWinConfetti(fresh.data?.multiplier || 10);
            } catch { /* silent */ }
        };

        poll();
        const id = setInterval(poll, POLL_MS);
        return () => { cancelled = true; clearInterval(id); clearTimeout(dismissTimerRef.current); };
    }, [user]);

    if (!user || user.role === 'admin') return null;
    const win = active?.data?.win;
    const mult = active?.data?.multiplier;
    const game = active?.data?.game_name;

    return (
        <AnimatePresence>
            {active && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="fixed inset-0 z-[100] pointer-events-none flex items-start justify-center pt-24"
                    data-testid="big-win-celebration"
                >
                    <motion.div
                        initial={{ y: -40, scale: 0.85, opacity: 0 }}
                        animate={{ y: 0, scale: 1, opacity: 1 }}
                        exit={{ y: -40, scale: 0.85, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 280, damping: 22 }}
                        onClick={dismiss}
                        className="pointer-events-auto cursor-pointer relative px-6 py-5 sm:px-10 sm:py-6 rounded-2xl shadow-2xl text-center max-w-[92vw]"
                        style={{
                            background: 'linear-gradient(135deg, rgba(255,215,0,0.18) 0%, rgba(57,255,20,0.14) 50%, rgba(255,0,127,0.18) 100%)',
                            border: '2px solid rgba(255,215,0,0.6)',
                            backdropFilter: 'blur(14px)',
                            WebkitBackdropFilter: 'blur(14px)',
                            boxShadow: '0 0 50px rgba(255,215,0,0.45), 0 0 90px rgba(255,0,127,0.25)',
                        }}
                    >
                        <button
                            onClick={(e) => { e.stopPropagation(); dismiss(); }}
                            className="absolute top-2 right-2 text-white/70 hover:text-white"
                            aria-label="Dismiss"
                            data-testid="big-win-dismiss"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        <div className="flex items-center justify-center gap-2 mb-1">
                            <Trophy className="w-5 h-5 text-[#FFD700]" />
                            <span className="text-[10px] sm:text-xs font-black tracking-[0.3em] text-[#FFD700]">BIG WIN</span>
                            <Sparkles className="w-5 h-5 text-[#FFD700]" />
                        </div>

                        <div
                            className="text-3xl sm:text-5xl font-black text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] tabular-nums"
                            data-testid="big-win-amount"
                        >
                            +{fmt$(win)}
                        </div>

                        {mult ? (
                            <div className="mt-1 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-black/30 border border-[#FFD700]/40">
                                <span className="text-xl sm:text-2xl font-black text-[#39FF14] tabular-nums">{mult}x</span>
                                <span className="text-[10px] sm:text-xs text-[#A0AAB2] font-bold uppercase tracking-wider">multiplier</span>
                            </div>
                        ) : null}

                        {game ? (
                            <p className="mt-2 text-xs sm:text-sm text-white/80 truncate max-w-[80vw]">
                                on <span className="font-bold text-[#FFD700]">{game}</span>
                            </p>
                        ) : null}

                        <p className="mt-3 text-[10px] text-white/50">tap to dismiss</p>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default BigWinCelebration;
